/**
 * Tax-return code totals (e.g. Canada GIFI for a T2 / T5013 return)
 * ------------------------------------------------------------------
 * Groups each account's balance (in DEBIT terms, as the Trial Balance computes it)
 * by the code chosen for the account. Several accounts can share one code; an
 * account with no code is listed apart so it can be given one. A code that is a
 * heading in the guide (e.g. GIFI 1000 "Cash and deposits") is still a code an
 * account may report on, so it is totalled like any other.
 *
 * Amounts are shown the way the return wants them: balance-sheet assets and
 * income-statement expenses are debit balances, liabilities / equity / revenue are
 * credit balances. `amount` is the balance on the code's natural side (positive =
 * normal), `net` stays in debit terms for checking.
 *
 * Pure + unit-tested (taxLineTotals.test.ts).
 */

export interface TaxTotalsAccount {
  coa_id: number;
  account_number: string | null;
  account_name: string;
  /** balance in debit terms (positive = debit) */
  net: number;
}

export interface TaxTotalsLine {
  line_code: string;
  line_name: string;
  line_group: string | null;
  statement: 'BS' | 'IS';
}

export interface TaxTotalRow {
  line_code: string;
  line_name: string;
  line_group: string;
  statement: 'BS' | 'IS';
  /** credit-side code (liabilities, equity, revenue): amounts shown as credit balances */
  credit_side: boolean;
  net: number;
  amount: number;
  accounts: TaxTotalsAccount[];
}

export interface TaxTotals {
  rows: TaxTotalRow[];
  /** accounts with a balance or activity and no code */
  uncoded: TaxTotalsAccount[];
  /** accounts whose code is not on the list any more */
  unknown: (TaxTotalsAccount & { line_code: string })[];
}

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Credit-side GIFI ranges: liabilities 2600–3499, equity 3500–3849, other comprehensive
 * income 7000–7099 (credit = gain), revenue 8000–8299, farm revenue 9370–9659.
 * Everything else (assets, cost of sales, expenses, taxes) is debit-side.
 */
export function isCreditSideCode(code: string): boolean {
  const n = parseInt(code, 10);
  if (isNaN(n)) return false;
  return (n >= 2600 && n < 3850) || (n >= 7000 && n < 7100) || (n >= 8000 && n < 8300) || (n >= 9370 && n < 9660);
}

export function taxLineTotals(
  accounts: TaxTotalsAccount[],
  codeByAccount: Map<number, string>,
  lines: TaxTotalsLine[],
): TaxTotals {
  const byCode = new Map(lines.map(l => [l.line_code, l]));
  const rows = new Map<string, TaxTotalRow>();
  const uncoded: TaxTotalsAccount[] = [];
  const unknown: (TaxTotalsAccount & { line_code: string })[] = [];
  for (const a of accounts) {
    const code = codeByAccount.get(Number(a.coa_id)) || '';
    if (!code) { uncoded.push(a); continue; }
    const line = byCode.get(code);
    if (!line) { unknown.push({ ...a, line_code: code }); continue; }
    let row = rows.get(code);
    if (!row) {
      row = {
        line_code: code, line_name: line.line_name, line_group: line.line_group || '', statement: line.statement,
        credit_side: isCreditSideCode(code), net: 0, amount: 0, accounts: [],
      };
      rows.set(code, row);
    }
    row.net = round2(row.net + (Number(a.net) || 0));
    row.accounts.push(a);
  }
  const out = Array.from(rows.values()).map(r => ({
    ...r,
    amount: round2(r.credit_side ? -r.net : r.net),
    accounts: r.accounts.slice().sort((x, y) => (x.account_number || '').localeCompare(y.account_number || '')),
  })).sort((x, y) => x.line_code.localeCompare(y.line_code));
  const byNumber = (x: TaxTotalsAccount, y: TaxTotalsAccount) => (x.account_number || '').localeCompare(y.account_number || '');
  return { rows: out, uncoded: uncoded.sort(byNumber), unknown: unknown.sort(byNumber) };
}

/** CSV: code, name, statement, amount, accounts — then the accounts with no code. */
export function taxTotalsCsv(formCode: string, totals: TaxTotals): string {
  const q = (v: unknown) => {
    const s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const acct = (a: TaxTotalsAccount) => `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}`;
  const lines = [[`${formCode} code`, 'Name', 'Statement', 'Amount', 'Side', 'Accounts'].map(q).join(',')];
  totals.rows.forEach(r => lines.push([
    r.line_code, r.line_name, r.statement === 'BS' ? 'Balance sheet' : 'Income statement',
    r.amount.toFixed(2), r.credit_side ? 'Credit' : 'Debit', r.accounts.map(acct).join('; '),
  ].map(q).join(',')));
  if (totals.uncoded.length || totals.unknown.length) {
    lines.push('');
    lines.push([`Accounts with no ${formCode} code`, '', '', 'Balance (debit +)'].map(q).join(','));
    totals.uncoded.forEach(a => lines.push([acct(a), '', '', a.net.toFixed(2)].map(q).join(',')));
    totals.unknown.forEach(a => lines.push([`${acct(a)} (code ${a.line_code} not on the list)`, '', '', a.net.toFixed(2)].map(q).join(',')));
  }
  return lines.join('\n') + '\n';
}
